import { TaskCode, getReferenceId } from '@quromedical/fhir-common';
import { compact, uniqby } from '@quromedical/utils';
import { Row } from 'components/base';
import { CrudForm, SubmissionHandler } from 'components/form';
import { Field, SelectOption } from 'components/types';
import { Button, IconName, Skeleton, SkeletonProvider, Text, Visible } from 'design-system';
import { Dialog, Fetcher } from 'design-system/components';
import { useBoolean } from 'hooks/useBoolean';
import { useDropdownUtilities } from 'hooks/useDropdownUtilities';
import { usePractitionerByOrgFetcher } from 'hooks/usePractitioners';
import { usePatientVitals } from 'providers/PatientVitalsProvider';
import React, { useCallback, useMemo } from 'react';
import { strings } from 'strings';
import { ObjectType } from 'validation';
import * as yup from 'yup';

import { codeDisplay } from './internal';

export interface AssignmentResult {
  code: TaskCode;
  performer: string;
  note: string;
  dueDate: Date;
}

interface FormData {
  code: TaskCode;
  performer: SelectOption<string, string>;
  note: string;
  dueDate: Date;
}

const formSchema = yup.object<ObjectType<FormData>>({
  code: yup.string().required().equals(Object.values(TaskCode)),
  performer: yup.object().required(),
  note: yup.string().required(),
  dueDate: yup.date().optional(),
});

export type AssigneeOption = SelectOption<string, string>;

export type AssignmentFetcher = Fetcher<string, string>;

export interface AssignmentProps {
  label?: string;
  codes: TaskCode[];
  errors?: string;
  isDisabled?: boolean;
  /**
   * Fetch any assignable entities
   */
  onChange: (result?: AssignmentResult) => void | Promise<void>;
  iconOnly?: boolean;
}

export const Assignment: React.FC<AssignmentProps> = ({
  label = strings.TaskAssignmentLabel,
  codes,
  errors,
  isDisabled,
  onChange,
  iconOnly = false,
}) => {
  const expanded = useBoolean(false);
  const context = usePatientVitals();
  const { patient, isValidatingPatient } = context;
  const { createListItems } = useDropdownUtilities();

  const organizations = useMemo(
    () => (!isValidatingPatient ? patient?.generalPractitioner.organizations || [] : []),
    [patient, isValidatingPatient]
  );

  const orgs = useMemo(
    () => (!isValidatingPatient ? compact(organizations?.map(getReferenceId)) : []),
    [organizations, isValidatingPatient]
  );

  const { options: practitionerByOrgOptions, isLoading } = usePractitionerByOrgFetcher(orgs);

  const organizationOptions = organizations.map<AssigneeOption>(organization => ({
    value: organization.reference as string,
    display: organization.display || strings.DisplayNotFound,
  }));

  const generalPractitioners = context.patient?.generalPractitioner.practitioners || [];

  const generalPractitionerOptions = generalPractitioners.map<AssigneeOption>(organization => ({
    value: organization.reference as string,
    display: organization.display || strings.DisplayNotFound,
  }));

  const practitioners = uniqby(
    [...practitionerByOrgOptions, ...generalPractitionerOptions],
    'value'
  );

  const options = createListItems([
    { heading: strings.FormLabelAssignedOrgOrTeams, items: organizationOptions },
    { heading: strings.GeneralPractitioners, items: practitioners },
  ]);

  const handleSubmit = useCallback<SubmissionHandler<FormData>>(
    async data => {
      try {
        await onChange({
          ...data,
          performer: data.performer.value,
        });

        expanded.setFalse();
        return {};
      } catch (error) {
        console.log('error', error);
        return { error };
      }
    },
    [onChange, expanded]
  );

  const title = label;

  const titleText = iconOnly ? undefined : title;

  const resolvedCodeOptions = codes.map<SelectOption>(code => ({
    display: codeDisplay[code],
    value: code,
  }));

  const icon: IconName = 'person-add';

  const onPress = expanded.setTrue;

  const fields: Field<FormData>[] = [
    {
      subfields: [
        {
          key: 'code',
          type: 'combobox-single',
          label: strings.TaskAssignmentLabelCode,
          fetcher: resolvedCodeOptions,
          searchable: true,
        },
        {
          key: 'performer',
          type: 'combobox-single',
          label: strings.TaskAssignmentLabelPerformer,
          returnFullOption: true,
          fetcher: options,
          searchable: true,
        },
      ],
    },
    {
      subfields: [
        {
          key: 'dueDate',
          type: 'date-component',
          maskType: 'date',
          label: strings.TaskLabelDueDate,
          validRange: { startDate: new Date() },
        },
      ],
    },
    {
      subfields: [
        {
          key: 'note',
          type: 'text-box',
          multiline: true,
          label: strings.TaskAssignmentNoteLabel,
        },
      ],
    },
  ];

  const hasOrganizations = orgs.length > 0;

  return (
    <>
      <Visible if={hasOrganizations}>
        <SkeletonProvider loading={isLoading}>
          <Skeleton>
            <Button
              iconPosition="right"
              disabled={isDisabled}
              onPress={onPress}
              icon={icon}
              variant="text"
              text={titleText}
            />
            <Row isVisible={!!errors}>
              <Text color="status-critical">{errors}</Text>
            </Row>
          </Skeleton>

          <Dialog isOpen={expanded.value} title={label}>
            <CrudForm<FormData>
              hasCard={false}
              initialValues={{}}
              validationSchema={formSchema}
              onSubmit={handleSubmit}
              onSecondarySubmit={expanded.setFalse}
              showSecondarySubmitButton
              secondaryButtonText={strings.ButtonTextClose}
              fields={fields}
            />
          </Dialog>
        </SkeletonProvider>
      </Visible>
    </>
  );
};
